import { lazy, Suspense } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import StickyCallBar from "@/components/StickyCallBar";
import { SERVICE_PAGES } from "@/lib/servicePages";
import { BUSINESS } from "@/lib/constants";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  Snowflake,
  Sparkles,
  ShieldCheck,
  ArrowUpRight,
} from "lucide-react";

const QuoteForm = lazy(() => import("@/components/QuoteForm"));
const Footer = lazy(() => import("@/components/Footer"));

const SectionFallback = () => (
  <div className="min-h-[240px] w-full" aria-hidden="true" />
);

export default function ServicePage() {
  const { slug } = useParams();
  const data = SERVICE_PAGES[slug];

  if (!data) {
    return <Navigate to="/" replace />;
  }

  const isWinter = data.theme === "winter";
  const accent = isWinter ? "#6EA8D0" : "#8AB273";
  const accentHover = isWinter ? "#88BCDC" : "#9DC585";
  const accentSoft = isWinter ? "#C2E0F2" : "#EDE4CF";

  const scrollToQuote = () => {
    const el = document.getElementById("quote");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const styleVars = {
    "--svc-accent": accent,
    "--svc-accent-hover": accentHover,
    "--svc-accent-soft": accentSoft,
  };

  return (
    <div
      data-testid={`service-page-${slug}`}
      className="bg-[#0a1210] text-white overflow-x-hidden"
      style={styleVars}
    >
      <Header onQuoteClick={scrollToQuote} />
      <main>
        <ServiceHero data={data} isWinter={isWinter} onQuoteClick={scrollToQuote} />
        <ServiceDetails data={data} isWinter={isWinter} onQuoteClick={scrollToQuote} />
        <RelatedServices data={data} isWinter={isWinter} />
        <Suspense fallback={<SectionFallback />}>
          <QuoteForm
            preselectedService={slug}
            source={`${data.title} · Service Page`}
            heading={
              <>
                Request your free
                <br />
                <span style={{ color: "var(--svc-accent)" }}>
                  {data.title.toLowerCase()}
                </span>{" "}
                quote.
              </>
            }
            intro={`Tell us about your property and we'll come back with a no-obligation quote. We'll know this request came from your interest in ${data.title}.`}
          />
          <Footer />
        </Suspense>
      </main>
      <StickyCallBar />
    </div>
  );
}

function ServiceHero({ data, isWinter, onQuoteClick }) {
  return (
    <section
      data-testid="service-hero"
      className="relative min-h-[88vh] w-full overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={data.hero}
          srcSet={`${data.hero}&w=800 800w, ${data.hero}&w=1600 1600w, ${data.hero}&w=2000 2000w`}
          sizes="100vw"
          alt=""
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="2000"
          height="1300"
          className="h-full w-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/55 to-[#0a1210]" />
        <div
          className="absolute inset-0"
          style={{
            background: isWinter
              ? "radial-gradient(ellipse at top right, rgba(110,168,208,0.18), transparent 55%)"
              : "radial-gradient(ellipse at top left, rgba(138,178,115,0.16), transparent 55%)",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 pt-36 lg:pt-44 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="ppc-reveal ppc-d-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full ppc-glass">
              {isWinter ? (
                <Snowflake size={14} style={{ color: "var(--svc-accent)" }} />
              ) : (
                <Sparkles size={14} style={{ color: "var(--svc-accent)" }} />
              )}
              <span
                className="label-eyebrow"
                style={{ color: "var(--svc-accent-soft)" }}
              >
                {data.season} · Niagara Region
              </span>
            </div>

            <h1
              data-testid="service-headline"
              className="font-display ppc-reveal ppc-d-2 mt-8 text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] font-extrabold leading-[0.95] tracking-tighter"
            >
              {data.title}
              <br />
              <span style={{ color: "var(--svc-accent)" }}>Done right.</span>
            </h1>

            <p className="ppc-reveal ppc-d-3 mt-6 text-base lg:text-xl text-white/55 max-w-xl uppercase tracking-widest">
              {data.tagline}
            </p>

            <p className="ppc-reveal ppc-d-3 mt-6 text-lg lg:text-xl text-white/75 max-w-2xl leading-relaxed">
              {data.description}
            </p>

            <div className="ppc-reveal ppc-d-4 mt-10 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="service-hero-quote"
                onClick={onQuoteClick}
                style={{ background: "var(--svc-accent)" }}
                className="group inline-flex items-center justify-center gap-2 text-black font-semibold px-6 py-4 rounded-full hover:opacity-90 transition-all"
              >
                {data.cta}
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
              <a
                href={BUSINESS.phoneHref}
                data-testid="service-hero-call"
                className="inline-flex items-center justify-center gap-2 ppc-glass px-6 py-4 rounded-full text-white font-medium hover:bg-white/5 transition"
              >
                <Phone size={16} style={{ color: "var(--svc-accent)" }} />
                Call {BUSINESS.phone}
              </a>
            </div>

            <div className="ppc-reveal ppc-d-5 mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm text-white/55">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={15} style={{ color: "var(--svc-accent)" }} /> Fully
                insured
              </span>
              <span>Niagara Region</span>
              <span>Residential + Commercial</span>
              <span>Free quotes</span>
            </div>
          </div>

          {/* Pricing card */}
          <div className="lg:col-span-4 ppc-reveal ppc-d-4">
            <div className="ppc-glass ppc-glow rounded-3xl p-7 lg:p-8">
              <div
                className="label-eyebrow"
                style={{ color: "var(--svc-accent-soft)" }}
              >
                Starting at
              </div>
              <div className="mt-4">
                <span
                  className="font-display text-3xl lg:text-4xl font-extrabold tracking-tighter"
                  style={{ color: "var(--svc-accent)" }}
                >
                  {data.priceFrom}
                </span>
              </div>
              <p className="mt-3 text-white/60 text-sm leading-relaxed">
                Final pricing is based on property size, access and scope.
                Every quote is free and no-obligation.
              </p>
              <div className="my-6 h-px w-full bg-white/10" />
              <button
                onClick={onQuoteClick}
                data-testid="service-hero-cta2"
                className="w-full bg-white text-black font-semibold py-3 rounded-full hover:bg-white/90 transition"
              >
                Request my free quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceDetails({ data, isWinter, onQuoteClick }) {
  return (
    <section
      data-testid="service-details"
      className="relative py-24 lg:py-32 border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <span
            className="label-eyebrow"
            style={{ color: "var(--svc-accent-soft)" }}
          >
            — What&apos;s included
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1]">
            Every job.
            <br />
            <span style={{ color: "var(--svc-accent)" }}>Same checklist.</span>
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            No corners cut. No surprise add-ons. Here&apos;s exactly what
            you get when you book {data.title.toLowerCase()}.
          </p>
          <button
            onClick={onQuoteClick}
            data-testid="service-details-cta"
            style={{ background: "var(--svc-accent)" }}
            className="mt-8 inline-flex items-center gap-2 text-black font-semibold px-6 py-3.5 rounded-full hover:opacity-90 transition"
          >
            {data.cta}
            <ArrowRight size={16} />
          </button>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3 lg:gap-4">
          {data.highlights.map((h, i) => (
            <div
              key={h}
              data-testid={`service-highlight-${i}`}
              className="rounded-2xl p-5 border border-white/10 bg-[#131c17] hover:-translate-y-0.5 transition-transform"
            >
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center mb-3"
                style={{ background: "var(--svc-accent)", color: "#0a1210" }}
              >
                <CheckCircle2 size={18} />
              </div>
              <div className="font-display text-base font-bold tracking-tight leading-snug">
                {h}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RelatedServices({ data, isWinter }) {
  const related = (data.related || [])
    .map((slug) => SERVICE_PAGES[slug])
    .filter(Boolean);
  if (!related.length) return null;

  return (
    <section
      data-testid="service-related"
      className="relative py-24 lg:py-32 bg-[#0d1614] border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <span
              className="label-eyebrow"
              style={{ color: "var(--svc-accent-soft)" }}
            >
              — Also by Premium Property Care
            </span>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter">
              Pair it with another service.
            </h2>
          </div>
          <Link
            to="/"
            data-testid="service-related-all"
            className="text-sm text-white/60 hover:text-white inline-flex items-center gap-2"
          >
            See all services <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {related.map((r) => (
            <Link
              key={r.slug}
              to={`/services/${r.slug}`}
              data-testid={`related-${r.slug}`}
              className="group relative overflow-hidden rounded-3xl border border-white/10 min-h-[280px] block"
            >
              <img
                src={r.hero}
                alt={r.title}
                loading="lazy"
                decoding="async"
                width="1400"
                height="1000"
                className="absolute inset-0 h-full w-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1100ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] uppercase tracking-[0.18em] border border-white/10">
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    background:
                      r.theme === "winter" ? "#6EA8D0" : "#8AB273",
                  }}
                />
                {r.season}
              </div>
              <div className="relative h-full p-5 lg:p-6 flex flex-col justify-end">
                <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight flex items-center justify-between gap-2">
                  {r.title}
                  <ArrowUpRight
                    size={18}
                    className="opacity-0 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                    style={{ color: "var(--svc-accent)" }}
                  />
                </h3>
                <p className="text-xs lg:text-sm text-white/60 mt-1.5">
                  {r.tagline}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
